// src/pages/WayliRawTablePage.jsx
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import * as XLSX from "xlsx";
import UserAccessSummary from "../components/layout/UserAccessSummary";
import { filterRowsByAccess } from "../utils/accessFilters";
import useAccessOverrideVersion from "../hooks/useAccessOverrideVersion";

const API_BASE = process.env.REACT_APP_API_URL || "https://api.portpilot.co";
const DATETIME_WITH_TIME_DEFAULT = new Set(["update_time", "updated_at"]);
const PAGE_SIZE_OPTIONS = [25, 50, 100, "ALL"];
const DATE_KEYWORDS = /(date|etd|eta|time|cutoff|cut_off|_add_)/i;
const DATE_EXCLUSIONS = new Set(["product_pl_id"]);

const arraysEqual = (a, b) => a.length === b.length && a.every((value, index) => value === b[index]);

const formatTimestampForFilename = (date) => {
  const pad = (n) => String(n).padStart(2, "0");
  return `${date.getFullYear()}${pad(date.getMonth() + 1)}${pad(date.getDate())}-${pad(date.getHours())}${pad(date.getMinutes())}${pad(date.getSeconds())}`;
};

const normaliseDateInput = (value) => {
  if (value === null || value === undefined || value === "") return null;
  if (value instanceof Date) return Number.isNaN(value.getTime()) ? null : value;
  if (typeof value === "number") {
    if (!Number.isFinite(value)) return null;
    if (value > 1e12) return new Date(value);
    if (value > 1e9) return new Date(value * 1000);
    return null;
  }
  if (typeof value === "string") {
    const trimmed = value.trim();
    if (!trimmed) return null;
    const isoLike = trimmed.replace(" ", "T");
    const parsed = new Date(isoLike);
    if (!Number.isNaN(parsed.getTime())) return parsed;
    const appended = new Date(`${isoLike}Z`);
    if (!Number.isNaN(appended.getTime())) return appended;
    return null;
  }
  try {
    const coerced = new Date(value);
    return Number.isNaN(coerced.getTime()) ? null : coerced;
  } catch {
    return null;
  }
};

const formatDateValue = (key, value, datetimeWithTime) => {
  const parsed = normaliseDateInput(value);
  if (!parsed) return value ?? "";
  const options = {
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  };
  if (datetimeWithTime.has(key)) {
    options.hour = "2-digit";
    options.minute = "2-digit";
    options.hour12 = false;
  }
  return new Intl.DateTimeFormat("en-US", options).format(parsed);
};

const isDateColumn = (key, datetimeWithTime) => {
  const normalised = String(key || "").toLowerCase();
  if (!normalised) return false;
  if (DATE_EXCLUSIONS.has(normalised)) return false;
  if (datetimeWithTime.has(key) || datetimeWithTime.has(normalised)) return true;
  return DATE_KEYWORDS.test(normalised);
};

const parseSortableValue = (key, value, datetimeWithTime) => {
  if (isDateColumn(key, datetimeWithTime)) {
    const parsedDate = normaliseDateInput(value);
    if (parsedDate) {
      return { type: "number", value: parsedDate.getTime() };
    }
  }
  if (value === null || value === undefined || value === "") {
    return { type: "number", value: Number.NEGATIVE_INFINITY };
  }
  if (typeof value === "number") {
    return { type: "number", value };
  }
  if (typeof value === "boolean") {
    return { type: "number", value: value ? 1 : 0 };
  }
  const text = String(value);
  const numeric = Number(text.replace(/,/g, ""));
  if (!Number.isNaN(numeric) && text.trim() !== "") {
    return { type: "number", value: numeric };
  }
  return { type: "string", value: text.toLowerCase() };
};

const shouldFormatAsDate = (key, value, datetimeWithTime) => {
  if (!isDateColumn(key, datetimeWithTime)) return false;
  if (value === null || value === undefined || value === "") return false;
  return Boolean(normaliseDateInput(value));
};

const normaliseDisplayValue = (value, key) => {
  if (value === null || value === undefined || value === "") return "";
  if (key === "order_status") {
    const num = Number(String(value).replace(/[^0-9-]/g, ""));
    if (!Number.isNaN(num)) return num;
  }
  if (typeof value === "number" || typeof value === "string") return value;
  if (typeof value === "boolean") return value ? "true" : "false";
  if (Array.isArray(value)) {
    return value
      .map((item) => {
        if (item === null || item === undefined || item === "") return "";
        if (typeof item === "number" || typeof item === "string") return item;
        if (typeof item === "boolean") return item ? "true" : "false";
        try {
          return JSON.stringify(item);
        } catch {
          return String(item);
        }
      })
      .join(", ");
  }
  if (typeof value === "object") {
    try {
      return JSON.stringify(value);
    } catch {
      return String(value);
    }
  }
  return String(value);
};

export default function WayliRawTablePage({
  title,
  endpoint,
  defaultColumnKeys: defaultColumnKeysProp,
  datetimeWithTime = DATETIME_WITH_TIME_DEFAULT,
  selectAllByDefault = false,
}) {
  const defaultColumns = useMemo(() => {
    if (Array.isArray(defaultColumnKeysProp) && defaultColumnKeysProp.length) {
      return [...defaultColumnKeysProp];
    }
    return [];
  }, [defaultColumnKeysProp]);

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [columns, setColumns] = useState([]);
  const [rows, setRows] = useState([]);
  const [visibleColumns, setVisibleColumns] = useState([]);
  const [sortState, setSortState] = useState({
    key: defaultColumns[0] || null,
    direction: defaultColumns.length ? -1 : 0,
  });
  const [searchTerm, setSearchTerm] = useState("");
  const [pageSize, setPageSize] = useState(50);
  const [page, setPage] = useState(1);
  const [columnMenuOpen, setColumnMenuOpen] = useState(false);
  const [lastUpdated, setLastUpdated] = useState(null);
  const [updatingMode, setUpdatingMode] = useState(null);

  const accessVersion = useAccessOverrideVersion();
  const accessFilteredRows = useMemo(() => {
    void accessVersion;
    return filterRowsByAccess(rows, {
      columns,
      custKeys: ["cust_code", "customer", "customer_code"],
      whsKeys: ["whse_code", "warehouse", "warehouse_code"],
      allowBlank: true,
    });
  }, [rows, columns, accessVersion]);
  const dropdownRef = useRef(null);
  const isMountedRef = useRef(true);

  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const response = await fetch(`${API_BASE}${endpoint}`);
      if (!response.ok) {
        const text = await response.text();
        throw new Error(text || "Failed to load data.");
      }
      const payload = await response.json();
      if (!payload?.ok) {
        throw new Error(payload?.error || "Failed to load data.");
      }
      const cols = payload.columns || (payload.rows?.length ? Object.keys(payload.rows[0]) : []);
      if (!isMountedRef.current) return;
      setColumns(cols);
      setRows(payload.rows || []);
      setVisibleColumns((current) => {
        if (selectAllByDefault) {
          return cols.slice();
        }
        if (current.length) {
          const filtered = current.filter((col) => cols.includes(col));
          if (filtered.length && arraysEqual(filtered, current)) return current;
          if (filtered.length) return filtered;
        }
        const defaults = defaultColumns.filter((col) => cols.includes(col));
        if (defaults.length) return defaults;
        return cols.slice(0, Math.min(10, cols.length));
      });
      setSortState((prev) => {
        if (prev.key && cols.includes(prev.key)) return prev;
        if (defaultColumns.length) {
          return { key: defaultColumns[0], direction: prev.direction || -1 };
        }
        return { key: null, direction: 0 };
      });
    } catch (err) {
      if (!isMountedRef.current) return;
      setColumns([]);
      setRows([]);
      setVisibleColumns([]);
      setError(err.message || "Failed to load data.");
    } finally {
      if (!isMountedRef.current) return;
      setLoading(false);
    }
  }, [endpoint, defaultColumns, selectAllByDefault]);



  const handleIncrementalLoad = useCallback(async () => {
    if (updatingMode) return;
    setUpdatingMode("incremental");
    setError("");
    try {
      const response = await fetch(`${API_BASE}/api/public/wayli-refresh?mode=incremental`, { method: "POST" });
      if (!response.ok) {
        const text = await response.text();
        throw new Error(text || "Failed to refresh Wayli data.");
      }
      const payload = await response.json();
      if (!payload?.ok) {
        throw new Error(payload?.error || "Failed to refresh Wayli data.");
      }
      await fetchData();
      setLastUpdated(new Date());
    } catch (err) {
      setError(err.message || "Failed to refresh Wayli data.");
    } finally {
      setUpdatingMode(null);
    }
  }, [fetchData, updatingMode]);

const handleFullLoad = useCallback(async () => {
    if (updatingMode) return;
    setUpdatingMode("full");
    setError("");
    try {
      const response = await fetch(`${API_BASE}/api/public/wayli-refresh?mode=full`, { method: "POST" });
      if (!response.ok) {
        const text = await response.text();
        throw new Error(text || "Failed to perform full load.");
      }
      const payload = await response.json();
      if (!payload?.ok) {
        throw new Error(payload?.error || "Failed to perform full load.");
      }
      await fetchData();
      setLastUpdated(new Date());
    } catch (err) {
      setError(err.message || "Failed to perform full load.");
    } finally {
      setUpdatingMode(null);
    }
  }, [fetchData, updatingMode]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  useEffect(() => {
    if (!columnMenuOpen) return undefined;
    const handleClick = (event) => {
      if (!dropdownRef.current) return;
      if (!dropdownRef.current.contains(event.target)) {
        setColumnMenuOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, [columnMenuOpen]);

  const visibleColumnSet = useMemo(() => new Set(visibleColumns), [visibleColumns]);
  const visibleColumnsKey = useMemo(() => visibleColumns.join("|"), [visibleColumns]);
  const accessFilteredCount = accessFilteredRows.length;

  const filteredRows = useMemo(() => {
    const term = searchTerm.trim().toLowerCase();
    if (!term) return accessFilteredRows;
    return accessFilteredRows.filter((row) =>
      columns.some((column) => {
        const value = row[column];
        if (value === null || value === undefined) return false;
        return String(value).toLowerCase().includes(term);
      })
    );
  }, [accessFilteredRows, searchTerm, columns]);

  const sortedRows = useMemo(() => {
    if (!sortState.key || sortState.direction === 0) return filteredRows;
    return [...filteredRows].sort((a, b) => {
      const left = parseSortableValue(sortState.key, a[sortState.key], datetimeWithTime);
      const right = parseSortableValue(sortState.key, b[sortState.key], datetimeWithTime);
      if (left.type !== right.type) {
        return sortState.direction * left.type.localeCompare(right.type);
      }
      if (left.type === "string") {
        return sortState.direction * left.value.localeCompare(right.value);
      }
      return sortState.direction * (left.value - right.value);
    });
  }, [filteredRows, sortState, datetimeWithTime]);

  const totalPages = useMemo(() => {
    if (!pageSize || pageSize === "ALL") return 1;
    return Math.max(1, Math.ceil(sortedRows.length / pageSize));
  }, [sortedRows, pageSize]);

  useEffect(() => {
    if (page > totalPages) {
      setPage(totalPages);
    }
  }, [page, totalPages]);

  useEffect(() => {
    setPage(1);
  }, [searchTerm, accessFilteredCount, visibleColumnsKey, pageSize]);

  const pagedRows = useMemo(() => {
    if (!pageSize || pageSize === "ALL") return sortedRows;
    const start = (page - 1) * pageSize;
    return sortedRows.slice(start, start + pageSize);
  }, [sortedRows, page, pageSize]);

  const pageStart = useMemo(() => {
    if (!sortedRows.length) return 0;
    if (pageSize === "ALL") return 1;
    return (page - 1) * pageSize + 1;
  }, [sortedRows.length, pageSize, page]);

  const pageEnd = useMemo(() => {
    if (!sortedRows.length) return 0;
    if (pageSize === "ALL") return sortedRows.length;
    return Math.min(page * pageSize, sortedRows.length);
  }, [sortedRows.length, pageSize, page]);

  const lastUpdatedLabel = lastUpdated
    ? new Intl.DateTimeFormat("en-US", {
        year: "numeric",
        month: "2-digit",
        day: "2-digit",
        hour: "2-digit",
        minute: "2-digit",
        second: "2-digit",
        hour12: false,
      }).format(lastUpdated)
    : "Never";

  const toggleSort = (column) => {
    setSortState((prev) => {
      if (prev.key !== column) return { key: column, direction: 1 };
      if (prev.direction === 1) return { key: column, direction: -1 };
      return { key: null, direction: 0 };
    });
  };

  const toggleColumn = (column) => {
    setVisibleColumns((prev) => {
      const alreadyVisible = prev.includes(column);
      if (alreadyVisible) {
        if (prev.length === 1) return prev;
        return prev.filter((col) => col !== column);
      }
      return [...prev, column];
    });
  };

  const selectAllColumns = () => setVisibleColumns(columns.slice());
  const setDefaultColumns = () => {
    if (selectAllByDefault || !defaultColumns.length) {
      setVisibleColumns(columns.slice());
      return;
    }
    const defaults = defaultColumns.filter((col) => columns.includes(col));
    setVisibleColumns(defaults.length ? defaults : columns.slice(0, Math.min(10, columns.length)));
  };

  const onExport = useCallback(() => {
    if (!visibleColumns.length || !sortedRows.length) return;
    const header = visibleColumns;
    const dataRows = sortedRows.map((row) =>
      visibleColumns.map((column) => {
        const value = row?.[column];
        if (shouldFormatAsDate(column, value, datetimeWithTime)) {
          return formatDateValue(column, value, datetimeWithTime);
        }
        return normaliseDisplayValue(value, column);
      })
    );
    const worksheet = XLSX.utils.aoa_to_sheet([header, ...dataRows]);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Wayli Export");
    const safeTitle = String(title || "Wayli_Raw").replace(/[^a-zA-Z0-9_-]+/g, "_");
    const filename = `${safeTitle}_${formatTimestampForFilename(new Date())}.xlsx`;
    XLSX.writeFile(workbook, filename);
  }, [visibleColumns, sortedRows, datetimeWithTime, title]);

  const exportDisabled = !visibleColumns.length || sortedRows.length === 0;

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl font-semibold text-slate-900">{title}</h1>
          <p className="text-sm text-slate-500">Explore Wayli {title} data with column controls.</p>
        </div>
        <UserAccessSummary />
      </div>

      {error && (
        <div className="rounded border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">Failed to load data: {error}</div>
      )}

      <div className="flex flex-col gap-3">
        <div className="flex flex-wrap items-center justify-between gap-3 text-sm text-slate-600">
          <div className="flex items-center gap-2">
            <input
              type="text"
              value={searchTerm}
              onChange={(event) => setSearchTerm(event.target.value)}
              placeholder="Search all columns..."
              className="w-64 rounded border border-slate-300 px-3 py-2 text-sm shadow-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
            />
            <button
              type="button"
              onClick={onExport}
              disabled={exportDisabled}
              className="rounded border border-slate-300 bg-white px-3 py-2 text-sm font-medium text-slate-700 shadow-sm hover:bg-slate-50 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-1 disabled:cursor-not-allowed disabled:opacity-40"
            >
              Export
            </button>
            <div className="relative" ref={dropdownRef}>
              <button
                type="button"
                onClick={() => setColumnMenuOpen((open) => !open)}
                className="rounded border border-slate-300 bg-white px-3 py-2 text-sm font-medium text-slate-700 shadow-sm hover:bg-slate-50 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-1"
              >
                Columns
              </button>
              {columnMenuOpen && (
                <div className="absolute right-0 z-20 mt-2 w-64 rounded-lg border border-slate-200 bg-white p-3 shadow-lg">
                  <div className="mb-2 flex items-center justify-between text-xs uppercase tracking-wide text-slate-400">
                    <span>Visible Columns</span>
                    <div className="flex gap-2">
                      <button type="button" className="text-blue-600 hover:underline" onClick={selectAllColumns}>
                        All
                      </button>
                      <button type="button" className="text-blue-600 hover:underline" onClick={setDefaultColumns}>
                        Default
                      </button>
                    </div>
                  </div>
                  <div className="max-h-72 space-y-1 overflow-y-auto pr-1 text-sm">
                    {columns.map((column) => (
                      <label key={column} className="flex cursor-pointer items-center gap-2 rounded px-2 py-1 hover:bg-slate-50">
                        <input
                          type="checkbox"
                          checked={visibleColumnSet.has(column)}
                          onChange={() => toggleColumn(column)}
                          className="accent-blue-600"
                        />
                        <span className="truncate text-slate-700" title={column}>
                          {column}
                        </span>
                      </label>
                    ))}
                    {!columns.length && <div className="py-4 text-center text-xs text-slate-400">No columns found.</div>}
                  </div>
                </div>
              )}
            </div>
          </div>
          <div className="flex items-center gap-3">
            <button
              type="button"
              onClick={handleIncrementalLoad}
              disabled={loading || updatingMode !== null}
              className="rounded border border-blue-600 bg-blue-600 px-3 py-2 text-sm font-medium text-white shadow-sm transition hover:bg-blue-700 disabled:cursor-not-allowed disabled:border-slate-300 disabled:bg-slate-200 disabled:text-slate-500"
            >
              {updatingMode === "incremental" ? "Incremental Loading..." : "Incremental Load"}
            </button>
            <button
              type="button"
              onClick={handleFullLoad}
              disabled={loading || updatingMode !== null}
              className="rounded border border-red-600 bg-red-600 px-3 py-2 text-sm font-medium text-white shadow-sm transition hover:bg-red-700 disabled:cursor-not-allowed disabled:border-slate-300 disabled:bg-slate-200 disabled:text-slate-500"
            >
              {updatingMode === "full" ? "Full Loading..." : "Full Load"}
            </button>
            {lastUpdated && (
              <div className="text-xs text-slate-500">
                Last update: <span className="font-medium text-slate-600">{lastUpdatedLabel}</span>
              </div>
            )}
          </div>
        </div>

        <div className="flex flex-wrap items-center justify-between gap-4 text-sm text-slate-600">
          <div className="flex items-center gap-2">
            <span>Rows per page:</span>
            <select
              value={pageSize}
              onChange={(event) => {
                const rawValue = event.target.value;
                const value = rawValue === "ALL" ? "ALL" : Number(rawValue);
                setPageSize(value === "ALL" ? "ALL" : Number.isFinite(value) && value > 0 ? value : 50);
                setPage(1);
              }}
              className="rounded border border-slate-300 px-2 py-1 text-sm shadow-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
            >
              {PAGE_SIZE_OPTIONS.map((option) => (
                <option key={option} value={option}>
                  {option}
                </option>
              ))}
            </select>
          </div>
          <div className="flex items-center gap-3">
            <span className="tabular-nums text-xs text-slate-500">
              {sortedRows.length === 0 ? 0 : pageStart} - {pageEnd} of {sortedRows.length}
            </span>
            <div className="flex items-center gap-1">
              <button
                type="button"
                className="rounded border border-slate-300 px-2 py-1 text-sm shadow-sm disabled:cursor-not-allowed disabled:opacity-40"
                onClick={() => setPage(1)}
                disabled={page <= 1 || pageSize === "ALL"}
              >
                {"<<"}
              </button>
              <button
                type="button"
                className="rounded border border-slate-300 px-2 py-1 text-sm shadow-sm disabled:cursor-not-allowed disabled:opacity-40"
                onClick={() => setPage((prev) => Math.max(1, prev - 1))}
                disabled={page <= 1 || pageSize === "ALL"}
              >
                {"<"}
              </button>
              <span className="tabular-nums text-sm">
                {page} / {totalPages}
              </span>
              <button
                type="button"
                className="rounded border border-slate-300 px-2 py-1 text-sm shadow-sm disabled:cursor-not-allowed disabled:opacity-40"
                onClick={() => setPage((prev) => Math.min(totalPages, prev + 1))}
                disabled={page >= totalPages || pageSize === "ALL"}
              >
                {">"}
              </button>
              <button
                type="button"
                className="rounded border border-slate-300 px-2 py-1 text-sm shadow-sm disabled:cursor-not-allowed disabled:opacity-40"
                onClick={() => setPage(totalPages)}
                disabled={page >= totalPages || pageSize === "ALL"}
              >
                {">>"}
              </button>
            </div>
          </div>
        </div>

        {loading ? (
          <div className="rounded-lg border border-dashed border-slate-300 bg-slate-50 px-6 py-12 text-center text-sm text-slate-500">
            Loading data...
          </div>
        ) : (
          <div className="max-h-[70vh] overflow-auto rounded-lg border border-slate-200 shadow-sm">
            <table className="min-w-full divide-y divide-slate-200 bg-white">
              <thead className="bg-slate-100">
                <tr>
                  {visibleColumns.map((column) => {
                    const isSorted = sortState.key === column && sortState.direction !== 0;
                    return (
                      <th
                        key={column}
                        onClick={() => toggleSort(column)}
                        className="sticky top-0 z-10 select-none bg-slate-100 px-4 py-2 text-left text-sm font-semibold uppercase tracking-wide text-slate-600 hover:bg-slate-200"
                      >
                        <div className="flex items-center gap-2">
                          <span>{column}</span>
                          {isSorted && <span className="text-xs text-slate-500">{sortState.direction === 1 ? "▲" : "▼"}</span>}
                        </div>
                      </th>
                    );
                  })}
                </tr>
              </thead>
              <tbody>
                {pagedRows.length ? (
                  pagedRows.map((row, rowIndex) => {
                    const rowKey = row.id ?? row._id ?? row._index ?? `row-${rowIndex}`;
                    return (
                      <tr key={rowKey} className="odd:bg-white even:bg-slate-50 hover:bg-blue-50">
                        {visibleColumns.map((column) => {
                          const value = row[column];
                          const displayValue = shouldFormatAsDate(column, value, datetimeWithTime)
                            ? formatDateValue(column, value, datetimeWithTime)
                            : normaliseDisplayValue(value, column);
                          const formatted =
                            typeof displayValue === "number" ? displayValue.toLocaleString("en-US") : displayValue;
                          return (
                            <td
                              key={`${column}-${rowKey}`}
                              className="whitespace-nowrap px-4 py-2 text-sm text-slate-700"
                              title={String(displayValue ?? "")}
                            >
                              {formatted}
                            </td>
                          );
                        })}
                      </tr>
                    );
                  })
                ) : (
                  <tr>
                    <td colSpan={visibleColumns.length || 1} className="px-4 py-6 text-center text-sm text-slate-500">
                      No records found.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
